// kfe-lesson-detail.js — lesson detail / "lesson chart".
//
// PORT TARGET: Classes/KFEModules/Main/LessonChartViewController.m (iOS) and
//              Assets/KFE/Scripts/Runtime/Core/UI/Lesson/LessonDetailScreen.cs
//
// Brief 023: linear list view of the items inside a lesson. Type-chip
// palette mirrors Bryan's `ii-sg-demo-plugin/assets/js/sg-demo-app.js`
// FILTER_TYPES (lines 373–384) so trial users (Bryan's WordPress plugin)
// and paying learners (this app) see the same conventions. We're NOT
// shipping the iOS 5-segment activity-wheel layout yet — that's a future
// polish brief.

import { KFEComponent } from "../core/component.js";
import { html } from "../core/html.js";
import { t, getLanguage } from "../core/i18n.js";
import { services } from "../platform-services.js";
import { loadLesson, loadPathway } from "../data/pathway-loader.js";
import { buildSingleFolderTree, getInaccessibleItems, gateStatesForFolder, isLessonComplete } from "../data/gating.js";
import { isGatingExemptId } from "../data/gating-config.js";
import { isLessonLocked } from "../data/pathway-builder.js";
import { isDevMode } from "../core/dev-mode.js";
import { navigate } from "../router.js";
import { ITEM_TYPE_GATE } from "../data/content-items.js";
import { loadExternalTitles, resolveItemTitle, resolveItemSubtitle } from "../data/item-titles.js";
import { isCompletableItem, resolveRequiredPlay } from "../data/item-completion.js";

/** @param {LessonItem} item */
function isGate(item) {
  // Lesson JSON uses the CMS string `"gate"`; legacy data has numeric
  // ITEM_TYPE_GATE (15). Recognise both so a gate is never rendered as a
  // tappable row.
  /** @type {any} */ const t = item.type;
  return t === "gate" || t === ITEM_TYPE_GATE;
}

// Meter item types. Lesson JSON labels every meter `"meter_test"`; legacy
// numeric data uses 17 (Dialogue), 18 (Listening), 19 (BrainSoaking). 16 is
// empty in the catalog but filtered defensively (Brief 055) to future-proof
// against any new type-16 emission.
const METER_TYPES = new Set([16, 17, 18, 19]);

/**
 * Meters are NEVER tappable menu rows (Brief 055). They fire modally from
 * kfe-exercise-host when a sibling item completes; here we drop them from the
 * visible list. The records still exist in the data.
 * @param {LessonItem} item
 */
function isMeter(item) {
  /** @type {any} */ const t = item.type;
  return t === "meter_test" || METER_TYPES.has(t);
}

const DEFAULT_PATHWAY_ID = "kfe_zero_to_fluency";

/**
 * The lesson number for the sub-header badge (Brief 099 §2). It's intrinsic to
 * the lesson — encoded in its title ("Lesson 440" / "第440课") per iOS canon —
 * so we pull the first integer out. Unnumbered orientation lessons → null
 * (badge hidden).
 * @param {string|undefined} title
 * @returns {number|null}
 */
function parseLessonNumber(title) {
  if (!title) return null;
  const m = /\d+/.exec(title);
  return m ? Number(m[0]) : null;
}

/**
 * Type-chip palette + label table. Hex values mirror Bryan's
 * ii-sg-demo-plugin/assets/js/sg-demo-app.js:373-384 (FILTER_TYPES). His
 * keys differ (e.g. l2b_training, haai, self_recording) but the colours
 * map by category. Passive-content types (music, video, audio, reading,
 * notes) all use Bryan's `content_item` colour (#3D6098).
 *
 * @typedef {{label: string, color: string}} TypeChip
 * @type {Record<string, TypeChip>}
 */
const TYPE_CHIP = {
  l2b_exercise:       { label: "L2B",      color: "#27ae60" },
  l2b_test:           { label: "L2T",      color: "#27ae60" },
  haai_exercise:      { label: "HAAI",     color: "#e67e22" },
  recording_exercise: { label: "SR",       color: "#8e44ad" },
  true_false:         { label: "T/F",      color: "#8e44ad" },
  multiple_choice:    { label: "MC",       color: "#2c3e50" },
  meter_test:         { label: "Meter",    color: "#2B4C7E" },
  situation_dialogue: { label: "Dialogue", color: "#3D6098" },
  music_track:        { label: "Music",    color: "#3D6098" },
  audio_video:        { label: "Audio",    color: "#3D6098" },
  video_lesson:       { label: "Video",    color: "#3D6098" },
  reading_lesson:     { label: "Read",     color: "#3D6098" },
  reference_note:     { label: "Note",     color: "#3D6098" },
  unknown:            { label: "?",        color: "var(--kfe-muted)" },
};

/**
 * New Word badge (Brief 109). Category K items carry numeric type 12 (shared
 * with Situation Dialogue), so a type-keyed chip alone mislabels them
 * "Dialogue". When `item.category === "K"` the badge is driven by the Category
 * instead: the canonical "New Word" label (shared with the Lesson Report bar
 * chart, Brief 098 `lessonReport.catNewWord`) on the New Word category hue
 * (`#7f8c8d`, same as kfe-lesson-report's CATEGORY_DEFS so the category reads
 * consistently across both surfaces). Label resolved at render time via t().
 * @type {{labelKey: string, color: string}}
 */
const NEW_WORD_CHIP = { labelKey: "lessonReport.catNewWord", color: "#7f8c8d" };

/**
 * @typedef {object} LessonItem
 * @property {string|number} id
 * @property {string} [type]
 * @property {string} [category]
 * @property {string} [titleEn]
 * @property {string} [titleZh]
 * @property {string} [gateRule]
 * @property {boolean} [isIntro]
 * @property {number} [requiredPlay]
 *
 * @typedef {object} LessonShape
 * @property {string} id
 * @property {string} [titleEn]
 * @property {string} [titleZh]
 * @property {LessonItem[]} [items]
 */

export class KFELessonDetail extends KFEComponent {
  static props = ["lessonId"];
  static styles = `
    :host { display: block; }

    /* Orange separator between the app header and the lesson sub-header
       (Brief 099 §3). Lesson-detail-only per iOS canon — lives on THIS screen,
       not the shared shell. Full column width. */
    .header-separator {
      height: var(--kfe-separator-orange-height);
      background: var(--kfe-separator-orange-color);
      margin: -1rem -1rem 0;   /* bleed past the <main> 1rem padding to full width */
    }

    /* Lesson sub-header (Brief 099 §2 / V1-UI-DESIGN-NOTES §7.1):
       «  | round lesson-number badge | "Lesson N" | "View the Report" | » */
    .sub-header {
      display: flex; align-items: center; gap: 0.5rem;
      padding: 0.6rem 0 0.75rem;
    }
    .nav-arrow {
      background: transparent; border: 0; color: var(--kfe-accent);
      font-size: 1.5rem; line-height: 1; cursor: pointer;
      min-width: 44px; min-height: 44px;   /* iOS HIG touch target */
      display: inline-flex; align-items: center; justify-content: center;
      flex: 0 0 auto;
    }
    /* Brief 135 NAV-2: the » arrow greys + de-activates when the next lesson is
       gated, instead of navigating to a locked lesson. Same dimming token the
       locked item rows / squiggle nodes use, so the affordance reads as locked
       consistently across the app. */
    .nav-arrow[disabled] {
      opacity: var(--kfe-locked-opacity); cursor: not-allowed; color: var(--kfe-muted);
    }
    .lesson-badge {
      flex: 0 0 auto;
      width: 1.9rem; height: 1.9rem; border-radius: 50%;
      border: 2px solid var(--kfe-accent); color: var(--kfe-fg);
      display: inline-flex; align-items: center; justify-content: center;
      font-weight: 700; font-size: 0.9rem;
    }
    .lesson-label { color: var(--kfe-fg); font-weight: 600; white-space: nowrap; }
    /* On narrow phones the full row (« + badge + label + "View the Report" + »)
       overflows and clips ». The text label is redundant (the round badge and
       the main header both show the number), so drop it < 420px to keep the
       nav arrows + report on-screen. Medium-constraint divergence from iOS
       §7.1 (CLAUDE.md Rule #9) — the badge stays. */
    @media (max-width: 420px) {
      .lesson-label { display: none; }
    }
    .view-report {
      color: var(--kfe-accent); text-decoration: none;
      font-size: 0.9rem; font-weight: 600;
      padding: 0.4rem 0.75rem;
      border: 1px solid var(--kfe-accent); border-radius: var(--kfe-radius);
      white-space: nowrap; flex: 0 0 auto;
      margin-left: auto;   /* push report + » to the right edge */
    }
    .view-report:hover { background: var(--kfe-surface-hover); }
    /* Brief 164 (Bug 3): the report link normally carries the margin-left:auto
       that pushes the trailing group (report + ») to the right edge. When it's
       hidden (incomplete lesson), pin the » arrow right instead so the header
       layout doesn't collapse leftward. */
    .sub-header:not(:has(.view-report)) .nav-arrow.next { margin-left: auto; }

    .item-list { list-style: none; padding: 0; margin: 0; }
    .item-row {
      display: flex; align-items: center; gap: 0.75rem;
      padding: 0.875rem 1rem;
      background: var(--kfe-surface);
      border-bottom: 1px solid var(--kfe-border);
      cursor: pointer;
    }
    .item-row:hover { background: var(--kfe-surface-hover); }
    .item-row.locked { opacity: var(--kfe-locked-opacity); cursor: not-allowed; }

    /* Two-line title + subtitle stack (Brief 099 §4). Both wrap — never
       ellipsis-truncate (iOS shows long titles on two lines). */
    .item-main { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
    .item-title { color: var(--kfe-fg); font-weight: 600; overflow-wrap: anywhere; }
    .item-subtitle { color: var(--kfe-muted); font-size: 0.8rem; overflow-wrap: anywhere; }

    /* Coloured chip; hex comes from TYPE_CHIP (matches Bryan's FILTER_TYPES). */
    .item-type {
      color: #fff;
      font-size: 0.75rem; font-weight: 600;
      text-transform: uppercase;
      padding: 2px 8px;
      border-radius: 4px;
      white-space: nowrap; flex: 0 0 auto;
    }

    /* Trailing completion indicator slot (Brief 099 §5), right side before
       where a chevron would sit. Fixed min width so chips/edges align whether
       a tick, an arc, or nothing renders. Empty for non-completable content. */
    .item-progress {
      flex: 0 0 auto; min-width: 2.25rem;
      display: inline-flex; align-items: center; justify-content: center;
    }
    .item-tick { color: var(--kfe-success); font-weight: 700; font-size: 1.1rem; }

    .gate-divider {
      text-align: center;
      padding: 0.5rem;
      color: var(--kfe-muted);
      border-top: 1px dashed var(--kfe-border);
      border-bottom: 1px dashed var(--kfe-border);
      pointer-events: none;
    }
    .gate-divider .lock { font-size: 1.1rem; }
    /* An open gate (upstream complete) reads as unlocked — dimmed so it
       recedes once passed, distinct from the solid 🔒 of a live barrier. */
    .gate-divider.open { opacity: 0.55; }

    .empty { color: var(--kfe-muted); padding: 2rem; text-align: center; }
  `;

  /** @type {LessonItem[]} */
  _items = [];
  /** @type {string | undefined} */
  _titleEn;
  /** @type {string | undefined} */
  _titleZh;
  /** @type {Set<string>} */
  _inaccessible = new Set();
  /** Per-gate open/locked state, keyed by gate id (Brief 102). Empty when
   * gating is off → every gate renders open.
   * @type {Map<string, "open" | "locked">} */
  _gateStates = new Map();
  _gatingEnabled = true;
  _loaded = false;

  /** Intra-lesson navigation (Brief 099 §2): adjacent lesson ids + ordinal.
   * `nextLocked` (Brief 135 NAV-2) is true when the next lesson is gated, so the
   * » arrow renders greyed + inert instead of jumping to a locked lesson.
   * @type {{prevId: string|null, nextId: string|null, number: number|null, nextLocked: boolean}} */
  _nav = { prevId: null, nextId: null, number: null, nextLocked: false };

  /** Per-item completion progress for completable rows (Brief 099 §5):
   * id → { completed, required }. Read once in onConnected.
   * @type {Map<string, {completed: number, required: number}>} */
  _progress = new Map();

  /** Brief 164 (Bug 3): is this lesson fully complete? Gates the "View the
   * Report" button per iOS canon. Recomputed live on item completion. */
  _lessonComplete = false;

  async onConnected() {
    const onI18n = () => {
      this._dispatchHeaderTitle();
      this._scheduleRender();
    };
    document.addEventListener("kfe:i18n", onI18n);
    this.addCleanup(() => document.removeEventListener("kfe:i18n", onI18n));

    // Brief 102: re-render when any item completes so a downstream gate the
    // learner just satisfied visually transitions to unlocked without a page
    // reload. learner-store.markItemCompleted dispatches `kfe:item-completed`
    // on document (the single completion chokepoint, mirroring
    // `kfe:setting-changed`). Registered via addCleanup to avoid handler
    // stacking on re-mount (CLAUDE.md Common Pitfall #1). Registered up here —
    // before any await / the empty-lessonId early return — so the subscription
    // exists regardless of which exit onConnected takes.
    document.addEventListener("kfe:item-completed", this._onItemCompleted);
    this.addCleanup(() => document.removeEventListener("kfe:item-completed", this._onItemCompleted));

    // Brief 143: a meter bump (Brief 125) raises an item's requiredPlay via
    // learner-store.applyPlaycountAdjustment, which fires `kfe:item-progress-changed`.
    // Re-read progress so the row's <kfe-play-progress> arc reflects the bumped
    // X/Y (e.g. 3/3 → 3/8) live, matching the audio player's inline refresh. Same
    // recompute path as item-completion; the arc reads `requiredPlay` from
    // `_progress` (repopulated by `_loadProgress`).
    document.addEventListener("kfe:item-progress-changed", this._onItemCompleted);
    this.addCleanup(() => document.removeEventListener("kfe:item-progress-changed", this._onItemCompleted));

    const lessonId = String(/** @type {any} */ (this).lessonId ?? "");
    if (!lessonId) {
      this._loaded = true;
      this._scheduleRender();
      return;
    }

    const activePathway = /** @type {string|undefined} */ (
      await services.learner.getSetting("active_pathway")
    );
    const pathwayId = activePathway || DEFAULT_PATHWAY_ID;

    try {
      const [lesson] = await Promise.all([
        /** @type {Promise<LessonShape>} */ (loadLesson(pathwayId, lessonId)),
        loadExternalTitles(),
      ]);
      this._items = Array.isArray(lesson.items) ? lesson.items : [];
      this._titleEn = lesson.titleEn;
      this._titleZh = lesson.titleZh;
    } catch (e) {
      console.warn(`[lesson-detail] failed to load ${pathwayId}/${lessonId}`, e);
      this._items = [];
    }

    this._gatingEnabled = (await services.learner.getSetting("gating_enabled")) !== false;
    // Load per-item progress BEFORE computing gating — the gate-state
    // derivation (Brief 102) reads each upstream item's completion from
    // `_progress`, so it must be populated first.
    await this._loadProgress();
    this._inaccessible = this._computeInaccessible();
    this._lessonComplete = this._computeLessonComplete();
    await this._loadNav(pathwayId, lessonId);
    this._loaded = true;

    this._dispatchHeaderTitle();
    this._scheduleRender();
  }

  /**
   * Resolve adjacent lessons + this lesson's number for the sub-header.
   * Mirrors kfe-lesson-report._resolveNav (Brief 097) but also returns prev +
   * the lesson number. The number is INTRINSIC to the lesson (iOS canon — the
   * lesson's own title is e.g. "Lesson 440" / "第440课"), NOT a positional
   * ordinal, so we parse it from the title; unnumbered orientation lessons get
   * no badge. Adjacency walks the numbered lessons only — gates aren't
   * navigable and intros are surfaced separately (CLAUDE.md intro rules), so
   * « / » move between numbered lessons. On the first numbered lesson `prevId`
   * is null (« hidden); on the last `nextId` is null (» hidden) — Brief 099 §2.
   * @param {string} pathwayId @param {string} lessonId
   */
  async _loadNav(pathwayId, lessonId) {
    try {
      const tree = await loadPathway(pathwayId);
      /** @type {string[]} */
      const flat = [];
      for (const stage of tree.stages || []) {
        for (const lesson of stage.lessons || []) {
          if (lesson && lesson.type !== "gate" && lesson.isIntro !== true) {
            flat.push(String(lesson.id));
          }
        }
      }
      const idx = flat.indexOf(String(lessonId));
      const nextId = idx >= 0 && idx < flat.length - 1 ? flat[idx + 1] : null;
      // Brief 135 NAV-2: grey the » arrow when the next lesson is gated. Dev mode
      // overrides (every destination stays live for content-walking). isLesson
      // Locked already short-circuits to false when gating is off.
      const nextLocked = !!nextId && !isDevMode() && (await isLessonLocked(pathwayId, nextId));
      this._nav = {
        prevId: idx > 0 ? flat[idx - 1] : null,
        nextId,
        number: parseLessonNumber(this._titleEn) ?? parseLessonNumber(this._titleZh),
        nextLocked,
      };
    } catch (e) {
      console.warn("[lesson-detail] nav resolve failed", e);
    }
  }

  /**
   * Read per-item completion state for completable rows (Brief 099 §5). X =
   * `completedCount` (the play counter, Brief 072); Y = requiredPlay resolved
   * via item-completion (authored item.requiredPlay ?? itemProgress.requiredPlay
   * ?? 1 — course-designer owned, Rule #8). Non-completable items are skipped.
   */
  async _loadProgress() {
    this._progress = new Map();
    for (const item of this._items) {
      if (!isCompletableItem(item)) continue;
      const numericId = Number(String(item.id).replace(/^item_/, ""));
      let prog;
      try {
        prog = /** @type {any} */ (await services.learner.getItemProgress(numericId));
      } catch { /* learner-store miss → treat as zero progress */ }
      const completed = prog?.completedCount ?? 0;
      const required = resolveRequiredPlay(item, prog?.requiredPlay);
      this._progress.set(String(item.id), { completed, required });
    }
  }

  /**
   * Compute both the inaccessible-item set and the per-gate open/locked state
   * from the learner's real per-item completion (Brief 102). Returns the
   * inaccessible set and, as a side effect, refreshes `_gateStates` so the
   * gate dividers can render their actual state.
   * @returns {Set<string>}
   */
  _computeInaccessible() {
    if (!this._gatingEnabled || this._items.length === 0) {
      this._gateStates = new Map();
      return new Set();
    }
    /** @type {import("../data/gating.js").ItemNode[]} */
    const nodes = this._items.map((it) => ({
      id: it.id,
      isGate: isGate(it),
      // Mark meters too: iOS treats them as gate-like dividers (types
      // 17/18/19). Their open/closed state derives from upstream completion
      // just like a gate (Brief 102) — the web build doesn't track a meter's
      // own completion (meters fire modally, Brief 055), so a self-count test
      // would never pass. Intro flag flows through so intros are skipped by
      // gating (CLAUDE.md §Intro lessons).
      isMeter: isMeter(it),
      isIntroItem: it.isIntro === true,
      // Brief 150: defensive — honour an exemption flag at item level too.
      isGatingExempt: isGatingExemptId(it.id, /** @type {any} */ (it).excludeFromGating),
      gateRule: /** @type {any} */ (it.gateRule),
      // Effective requiredPlay = max(authored, meter-bumped) for completable
      // items, so a Listening/Dialogue meter bump re-closes the gate it feeds
      // (Brief 125 — iOS reads the bumped required_play in the gate query). The
      // resolved value lives in `_progress` (populated by _loadProgress via
      // resolveRequiredPlay, which takes the max and floors at 1). Non-completable
      // items aren't in `_progress`; for them pass the raw authored value
      // verbatim — INCLUDING 0, which makes content auto-satisfy the upstream
      // predicate (count 0 >= required 0). Pre-125 this used the raw authored
      // value for everything, which masked the bump (the song stayed at its
      // authored 3 no matter how low the learner metered).
      requiredPlay: this._progress.get(String(it.id))?.required
        ?? (typeof it.requiredPlay === "number" ? it.requiredPlay : undefined),
    }));
    // Brief 102: feed the learner's real per-item completion so a gate's
    // upstream-derived state reflects what's actually been played. `_progress`
    // (populated by _loadProgress) holds completable items' completedCount;
    // content items (requiredPlay 0) aren't in it but auto-satisfy via the 0
    // threshold above.
    /** @type {Record<string, number>} */
    const completedCounts = {};
    for (const [id, p] of this._progress) completedCounts[id] = p.completed;
    const folderId = "__lesson_" + String(/** @type {any} */ (this).lessonId);
    const tree = buildSingleFolderTree(folderId, nodes);
    const learnerState = { gatingEnabled: true, completedCounts };
    const ids = getInaccessibleItems(folderId, learnerState, tree);
    this._gateStates = gateStatesForFolder(nodes, learnerState);
    return new Set(ids.map(String));
  }

  /** Document `kfe:item-completed` handler (Brief 102). Arrow-bound so the
   * same reference is added and removed via addCleanup. */
  _onItemCompleted = () => { this._recomputeGating(); };

  /**
   * Re-read progress, recompute gating, re-render. Called when an item
   * completes (Brief 102) so a satisfied gate flips downstream rows from
   * locked → clickable live. Guarded against detached-state writes (Common
   * Pitfall #2): if the screen has been removed mid-await, bail before
   * touching render state.
   */
  async _recomputeGating() {
    if (!this._loaded) return;
    await this._loadProgress();
    if (!this._connected) return;
    this._inaccessible = this._computeInaccessible();
    this._lessonComplete = this._computeLessonComplete();
    this._scheduleRender();
  }

  /**
   * Brief 164 (Bug 3): is this lesson fully complete? Two principles separated:
   *
   *   1. **Viewing the report is NOT a step in opening any gate.** The report is
   *      read-only — looking at it never contributes to the learner's progression
   *      through a lesson, a stage, or a pathway.
   *   2. **"The terminal gate at the end of this lesson has opened" IS the
   *      criterion for SHOWING the in-lesson 'View the Report' button.** I.e.
   *      the report appears precisely when the lesson is complete + the next
   *      lesson is unlocked.
   *
   * Brief 097 conflated these into the wrong rule ("the report doesn't gate
   * progression" → "the button is always visible"). That was a misunderstanding;
   * Brief 164 corrects it. Brief 097's design-note assertion ("always available")
   * is superseded.
   *
   * iOS canon agrees structurally: `LessonChartViewController.m:283-309`
   * (`updateReportButtonsForCompletedLessons`) + `KFELessonInfoView.m:26` —
   * the button is hidden by default and shown only when the lesson's folder is
   * marked complete (`app_item_info.completed`). Rule #9 + Chris's 2026-06-18
   * reframe both land here.
   *
   * Derived LIVE from `_progress` (not the roll-up cache) so the button appears
   * the instant the learner finishes the last item on this very screen, without
   * waiting for the stage-screen roll-up refresh. Uses the SAME pure predicate
   * (`isLessonComplete`) the squiggle's `deriveLessonComplete` and the
   * `lesson_completed` event use, so the button and the squiggle's green node can
   * never disagree. Independent of gating (iOS shows the report on completion
   * regardless of gating state).
   * @returns {boolean}
   */
  _computeLessonComplete() {
    if (this._items.length === 0) return false;
    /** @type {import("../data/gating.js").ItemNode[]} */
    const nodes = this._items.map((it) => ({
      id: it.id,
      isGate: isGate(it),
      isMeter: isMeter(it),
      isIntroItem: it.isIntro === true,
      isGatingExempt: isGatingExemptId(it.id, /** @type {any} */ (it).excludeFromGating),
      requiredPlay: this._progress.get(String(it.id))?.required
        ?? (typeof it.requiredPlay === "number" ? it.requiredPlay : undefined),
    }));
    /** @type {Record<string, number>} */
    const completedCounts = {};
    for (const [id, p] of this._progress) completedCounts[id] = p.completed;
    return isLessonComplete(nodes, { completedCounts });
  }

  /** @param {LessonItem} item */
  _isItemLocked(item) {
    if (!this._gatingEnabled) return false;
    return this._inaccessible.has(String(item.id));
  }

  _goPrev = () => { if (this._nav.prevId) navigate(`/lesson/${this._nav.prevId}`); };
  // Brief 135 NAV-2: never navigate into a locked next lesson (the button is
  // also rendered disabled, but guard here too in case it's activated some
  // other way).
  _goNext = () => { if (this._nav.nextId && !this._nav.nextLocked) navigate(`/lesson/${this._nav.nextId}`); };

  _dispatchHeaderTitle() {
    const lang = getLanguage();
    const title = (lang === "zh-Hans" && this._titleZh)
      ? this._titleZh
      : (this._titleEn || t("lesson.title", { id: String(/** @type {any} */ (this).lessonId ?? "?") }));
    this.dispatchEvent(new CustomEvent("kfe:set-header-title", {
      detail: { title },
      bubbles: true,
      composed: true,
    }));
  }

  /** @param {string|number} itemId */
  _open(itemId) {
    const id = String(itemId).replace(/^item_/, "");
    // Brief 095: stash the lesson the learner is entering so the completion
    // funnel (learner-store.markItemCompleted) can resolve lesson context to
    // fire `lesson_completed` + record HAAI Recognition-Index history. This is
    // the only entry point to an exercise (squiggle/pathway route to lessons,
    // lessons open exercises), so one write here covers every exercise launch.
    const lessonId = String(/** @type {any} */ (this).lessonId ?? "");
    if (lessonId) {
      services.learner.setSetting("active_lesson", lessonId)
        .catch((err) => console.warn("[lesson-detail] active_lesson write failed", err));
    }
    navigate(`/exercise/${id}`);
  }

  /** @param {LessonItem} item */
  _renderRow(item) {
    if (isGate(item)) {
      // Brief 102: the divider reflects the gate's derived state. Open once
      // every upstream item is complete (🔓), locked otherwise (🔒). With
      // gating off there are no gate states → render open.
      const open = !this._gatingEnabled || this._gateStates.get(String(item.id)) !== "locked";
      return open
        ? html`<li class="gate-divider open"><span class="lock">🔓</span></li>`
        : html`<li class="gate-divider"><span class="lock">🔒</span></li>`;
    }
    const isLocked = this._isItemLocked(item);
    const title = resolveItemTitle(/** @type {any} */ (item));
    const subtitle = resolveItemSubtitle(/** @type {any} */ (item));
    // Brief 109: Category K (New Word) overrides the type-keyed chip — type 12
    // is shared with Situation Dialogue, so dispatching on type alone mislabels
    // single-word items "Dialogue".
    const chip = String(item.category || "").toUpperCase() === "K"
      ? { label: t(NEW_WORD_CHIP.labelKey), color: NEW_WORD_CHIP.color }
      : (TYPE_CHIP[String(item.type)] || TYPE_CHIP.unknown);
    const prog = this._progress.get(String(item.id));
    const isComplete = prog ? prog.completed >= prog.required : false;

    const li = document.createElement("li");
    li.className = "item-row" + (isLocked ? " locked" : "") + (isComplete ? " completed" : "");
    if (!isLocked) {
      li.addEventListener("click", () => this._open(item.id));
    }

    // Title + subtitle stack (Brief 099 §4). Subtitle line only when present.
    const main = document.createElement("div");
    main.className = "item-main";
    const titleEl = document.createElement("span");
    titleEl.className = "item-title";
    titleEl.textContent = title;
    main.appendChild(titleEl);
    if (subtitle) {
      const subEl = document.createElement("span");
      subEl.className = "item-subtitle";
      subEl.textContent = subtitle;
      main.appendChild(subEl);
    }

    const chipEl = document.createElement("span");
    chipEl.className = "item-type";
    chipEl.style.background = chip.color;
    chipEl.textContent = chip.label;

    // Trailing completion indicator (Brief 099 §5). Completable + done → ✓;
    // completable + in-progress → <kfe-play-progress X/Y>; content → empty slot.
    const progress = document.createElement("span");
    progress.className = "item-progress";
    if (prog) {
      if (isComplete) {
        progress.classList.add("item-tick");
        progress.textContent = "✓";
      } else {
        const arc = document.createElement("kfe-play-progress");
        /** @type {any} */ (arc).completed = prog.completed;
        /** @type {any} */ (arc).required = prog.required;
        progress.appendChild(arc);
      }
    }

    li.append(main, chipEl, progress);
    return li;
  }

  render() {
    if (!this._loaded) {
      return html`<p class="empty">${t("loading")}</p>`;
    }
    const lessonId = String(/** @type {any} */ (this).lessonId ?? "");
    // Lesson sub-header (Brief 099 §2/§3): orange separator, « prev, round
    // lesson-number badge, "Lesson N" label, "View the Report", » next.
    // Brief 164 (Bug 3): "View the Report" is HIDDEN until the lesson is fully
    // complete, mirroring iOS (LessonChartViewController.m — the report button
    // is shown only for completed lessons). This reverses the Brief 097
    // always-available choice; iOS canon wins (Rule #9). Arrows hide (not grey)
    // at stage boundaries per iOS canon.
    const n = this._nav.number;
    const prevArrow = this._nav.prevId
      ? html`<button class="nav-arrow prev" type="button" aria-label="${t("lessonDetail.prevLesson")}" on:click=${this._goPrev}>«</button>`
      : "";
    // Brief 135 NAV-2: a gated next lesson renders the » arrow greyed + disabled
    // with a "complete this lesson first" tooltip, rather than navigating into a
    // locked lesson. Unlocked (or dev mode) → the normal live arrow.
    const nextArrow = !this._nav.nextId
      ? ""
      : this._nav.nextLocked
        ? html`<button class="nav-arrow next" type="button" disabled
                aria-label="${t("lessonDetail.nextLessonLocked")}"
                title="${t("lessonDetail.nextLessonLocked")}">»</button>`
        : html`<button class="nav-arrow next" type="button" aria-label="${t("lessonDetail.nextLesson")}" on:click=${this._goNext}>»</button>`;
    // Brief 164 (Bug 3): only render the report link once the lesson is complete.
    const reportLink = this._lessonComplete
      ? html`<a class="view-report" href="/lesson/${lessonId}/report">${t("lessonDetail.viewReport")}</a>`
      : "";
    const subHeader = lessonId
      ? html`
          <div class="header-separator"></div>
          <div class="sub-header">
            ${prevArrow}
            ${n != null ? html`<span class="lesson-badge">${n}</span>` : ""}
            ${n != null ? html`<span class="lesson-label">${t("lessonDetail.lessonNumber", { n: String(n) })}</span>` : ""}
            ${reportLink}
            ${nextArrow}
          </div>`
      : "";
    // Meters never render as tappable rows (Brief 055); drop them here.
    const visible = this._items.filter((it) => !isMeter(it));
    const body = visible.length === 0
      ? html`<p class="empty">${t("lesson.empty")}</p>`
      : html`<ul class="item-list">${visible.map((it) => this._renderRow(it))}</ul>`;
    return html`${subHeader}${body}`;
  }
}
